import React, { useCallback, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import LegacyOverviewCard from "@/modules/racket_lab_admin/LegacyOverviewCard";
import LegacyClassifyForm from "@/modules/racket_lab_admin/LegacyClassifyForm";
import LegacyBatchPicker from "@/modules/racket_lab_admin/LegacyBatchPicker";
import DryRunDiffPanel from "@/modules/racket_lab_admin/DryRunDiffPanel";
import ApplyConfirmStrip from "@/modules/racket_lab_admin/ApplyConfirmStrip";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import useDryRunApply from "@/hooks/useDryRunApply";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

const Tab = ({ active, onClick, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-t-md border-b-2 px-3 py-1.5 text-xs font-medium ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
  >{label}</button>
);

const ClassifySection = () => {
  const t = useRacketLabAdminT();
  const [intent, setIntent] = useState({
    user_id: "", bucket: "SOFT_REVOKE", reason: "",
    role: "shop_manager", organisation_id: "TEAMSOFTWAREINNOVATIONS", shop_ids: [],
  });
  const buildPayload = useCallback(() => {
    const body = { user_id: intent.user_id?.trim() || "", bucket: intent.bucket, reason: intent.reason || null };
    if (intent.bucket === "BACKFILL") {
      body.role = intent.role; body.organisation_id = intent.organisation_id; body.shop_ids = intent.shop_ids || [];
    }
    return body;
  }, [intent]);
  const { dryRun, applyState, error, loading, stale, runDryRun, applyAction, reset } = useDryRunApply({
    endpoint: "/admin/racket-lab/_ops/legacy-memberships-classify", buildPayload,
  });
  const canDryRun = Boolean(intent.user_id?.trim()) && !loading;
  const applyDisabled = !dryRun || stale || dryRun?.kill_switch_active === true || loading;
  const disabledReason = !dryRun
    ? t("ops.common.run_dry_run_first")
    : stale
      ? t("ops.common.input_changed_rerun")
      : dryRun.kill_switch_active
        ? t("ops.common.kill_switch_blocked")
        : loading
          ? t("ops.common.in_flight")
          : null;
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <LegacyClassifyForm value={intent} onChange={setIntent} disabled={loading} />
        <div className="mt-3 flex gap-2">
          <button type="button" data-testid="rl-ops-legacy-classify-dry-run-btn" disabled={!canDryRun} onClick={runDryRun} className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canDryRun ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"}`}>{t("ops.promote.dry_run_btn")}</button>
          <button type="button" onClick={reset} disabled={loading} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">{t("ops.promote.reset_btn")}</button>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <DryRunDiffPanel dryRun={dryRun} stale={stale} dataTestId="rl-ops-legacy-classify-dry-result" />
        <ApplyConfirmStrip disabled={applyDisabled} disabledReason={disabledReason} onApply={() => applyAction({})} confirmPhrase="APPLY" applyLabel={t("ops.legacy.apply_classify")} dataTestId="rl-ops-legacy-classify-apply-strip" />
        {error && <div data-testid="rl-ops-legacy-classify-error" className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">{t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}</div>}
        {applyState && <div data-testid="rl-ops-legacy-classify-apply-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3"><p className="mb-2 text-xs font-medium text-emerald-900">{t("ops.legacy.classify.applied_line", { action: applyState.action, bucket: applyState.bucket })}</p><JSONInspector value={applyState} dataTestId="rl-ops-legacy-classify-apply-json" /></div>}
      </div>
    </section>
  );
};

const BatchSection = () => {
  const t = useRacketLabAdminT();
  const [batch, setBatch] = useState({ raw: "", items: [], valid: false });
  const buildPayload = useCallback(() => ({ items: batch.items || [] }), [batch.items]);
  const { dryRun, applyState, error, loading, stale, runDryRun, applyAction, reset } = useDryRunApply({
    endpoint: "/admin/racket-lab/_ops/legacy-memberships-apply-batch", buildPayload,
  });
  const canDryRun = batch.valid && !loading;
  const applyDisabled = !dryRun || stale || dryRun?.kill_switch_active === true || loading;
  const disabledReason = !dryRun
    ? t("ops.common.run_dry_run_first")
    : stale
      ? t("ops.common.input_changed_rerun")
      : dryRun?.kill_switch_active
        ? t("ops.common.kill_switch_blocked")
        : loading
          ? t("ops.common.in_flight")
          : null;
  return (
    <section className="flex flex-col gap-4">
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <LegacyBatchPicker value={batch} onChange={setBatch} disabled={loading} />
        <div className="mt-3 flex gap-2">
          <button type="button" data-testid="rl-ops-legacy-batch-dry-run-btn" disabled={!canDryRun} onClick={runDryRun} className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canDryRun ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"}`}>{t("ops.legacy.batch.dry_run_btn")}</button>
          <button type="button" onClick={reset} disabled={loading} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">{t("ops.promote.reset_btn")}</button>
        </div>
      </div>
      <DryRunDiffPanel dryRun={dryRun} stale={stale} dataTestId="rl-ops-legacy-batch-dry-result" />
      <ApplyConfirmStrip disabled={applyDisabled} disabledReason={disabledReason} onApply={() => applyAction({})} confirmPhrase="APPLY-BATCH" applyLabel={t("ops.legacy.apply_batch")} dataTestId="rl-ops-legacy-batch-apply-strip" />
      {error && <div data-testid="rl-ops-legacy-batch-error" className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">{t("ops.common.error_prefix")} {error.status || ""}: {error.detail || t("ops.common.error_unknown")}</div>}
      {applyState && (
        <div data-testid="rl-ops-legacy-batch-apply-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="mb-2 text-xs font-medium text-emerald-900">
            {t("ops.legacy.batch.applied_line", { run_id: applyState.run_id, applied: applyState.counts?.applied ?? 0, failed: applyState.counts?.failed ?? 0 })}
          </p>
          <Link to={`/admin/racket-lab/ops/legacy/runs/${applyState.run_id}`} data-testid="rl-ops-legacy-batch-run-report-link" className="text-xs text-emerald-700 underline">{t("ops.legacy.batch.view_run_report")}</Link>
          <JSONInspector value={applyState} dataTestId="rl-ops-legacy-batch-apply-json" />
        </div>
      )}
    </section>
  );
};

const RLAdminOpsLegacy = () => {
  const t = useRacketLabAdminT();
  const [tab, setTab] = useState("overview");
  return (
    <div data-testid="rl-admin-ops-legacy-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <Link to="/admin/racket-lab/ops" data-testid="rl-ops-legacy-back" className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"><ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}</Link>
      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("ops.legacy.page_title")}</h1>
        <p className="text-xs text-slate-500">{t("ops.legacy.subtitle")}</p>
      </header>
      <nav className="flex gap-2 border-b border-slate-200">
        <Tab active={tab === "overview"} onClick={() => setTab("overview")} label={t("ops.legacy.tabs.overview")} testId="rl-ops-legacy-tab-overview" />
        <Tab active={tab === "classify"} onClick={() => setTab("classify")} label={t("ops.legacy.tabs.classify")} testId="rl-ops-legacy-tab-classify" />
        <Tab active={tab === "batch"} onClick={() => setTab("batch")} label={t("ops.legacy.tabs.batch")} testId="rl-ops-legacy-tab-batch" />
      </nav>
      {tab === "overview" && <LegacyOverviewCard dataTestId="rl-ops-legacy-overview" />}
      {tab === "classify" && <ClassifySection />}
      {tab === "batch" && <BatchSection />}
    </div>
  );
};

export default RLAdminOpsLegacy;
